// apps/web/src/features/call-center/CallCenterSelfServicePage.tsx
//
// Self-service own-leads screen for the external `call_center_agent` (#1398).
// Replaces the old single-form intake page: a read-only table of the agent's
// OWN accepted submissions (`useMyCallCenterLeads`) inside the WIDE confinement
// layout (no AppShell, no sidebar, logout-only), a "New lead" `EditorDrawer`
// hosting the reusable form, a read-only detail drawer on row click, and a
// debounced server-side phone search.
//
// DANGER: external boundary — the table/detail render BLIND snapshot fields
// only; no opaque id, no live lead/public code/status, no detail fetch.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CallCenterMyLeadsItem } from '@magimp/shared';
import { useMyCallCenterLeads } from '@magimp/shared-hooks';
import { useNarrow } from '../../ui/index.js';
import { useToastStore } from '../../stores/toast.js';
import { CallCenterIntakeLayout } from './CallCenterIntakeLayout.js';
import { IntakeLogoutButton } from './IntakeLogoutButton.js';
import { CallCenterLeadsHeader } from './components/CallCenterLeadsHeader.js';
import { CallCenterLeadsTable } from './components/CallCenterLeadsTable.js';
import { CallCenterNewLeadDrawer } from './components/CallCenterNewLeadDrawer.js';
import { CallCenterLeadDetailDrawer } from './components/CallCenterLeadDetailDrawer.js';

const SEARCH_DEBOUNCE_MS = 300;

export function CallCenterSelfServicePage() {
  const { t } = useTranslation('callCenter');
  const narrow = useNarrow();
  const showToast = useToastStore((s) => s.show);

  // WHY: debounce the raw input so each keystroke does not spawn a server
  //   query; the trimmed, debounced value is the `phone` filter feeding the
  //   query key (a new value starts a fresh cursor walk — search is
  //   server-side over the whole history).
  const [search, setSearch] = useState('');
  const [debouncedPhone, setDebouncedPhone] = useState<string | undefined>(undefined);
  useEffect(() => {
    const trimmed = search.trim();
    const id = setTimeout(
      () => setDebouncedPhone(trimmed === '' ? undefined : trimmed),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [search]);

  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState<CallCenterMyLeadsItem | null>(null);

  const query = useMyCallCenterLeads(debouncedPhone ? { phone: debouncedPhone } : {});
  const leads = useMemo<readonly CallCenterMyLeadsItem[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  return (
    <CallCenterIntakeLayout variant="wide" header={<IntakeLogoutButton />}>
      <CallCenterLeadsHeader
        narrow={narrow}
        search={search}
        onSearchChange={setSearch}
        onNewLead={() => setNewOpen(true)}
      />
      <CallCenterLeadsTable
        leads={leads}
        narrow={narrow}
        loading={query.isPending}
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onOpenLead={setDetail}
        onLoadMore={() => void query.fetchNextPage()}
      />
      {newOpen && (
        <CallCenterNewLeadDrawer
          narrow={narrow}
          onClose={() => setNewOpen(false)}
          onSubmitted={() => {
            showToast({ tone: 'success', text: t('selfService.submitSuccess') });
            setNewOpen(false);
          }}
        />
      )}
      {detail && (
        <CallCenterLeadDetailDrawer narrow={narrow} lead={detail} onClose={() => setDetail(null)} />
      )}
    </CallCenterIntakeLayout>
  );
}
